import React, { useEffect, useRef, useState } from "react";
import { Flex, Form, Input, Select, Spin, Typography } from "antd";
import { ContactOption } from "../../index";
import { useTranslation } from "react-i18next";
import {
  BlockOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { KeyRound } from "@/components/icon";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { axiosInstance } from "@/utilities";
import { debounce } from "@/utilities/debounce";
import { PhoneNumberInput } from "@/components/phone-number-input";

const { Text } = Typography;
const { Option } = Select;

type OptionType = { label: string; value: string };

type StorePermissionsCardProps = {
  store?: { name?: string; code?: string; link?: string };
  selectedContact?: ContactOption;
  isNewUser?: boolean;
};

export const OperatorPermissionsCard: React.FC<StorePermissionsCardProps> = ({
  store,
  selectedContact,
  isNewUser,
}) => {
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 20,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [operators, setOperators] = useState<any>([]);
  const storeIdentifier = encodeURIComponent(
    store?.name?.replace(/"/g, "").toLowerCase() || "",
  );

  const { t } = useTranslation();

  const getOperators = async (loadMore: boolean = false) => {
    if (loadingOperators) return;

    setLoadingOperators(true);
    try {
      const { currentPage, pageSize } = loadMore
        ? { ...pagination, currentPage: pagination.currentPage + 1 }
        : pagination;

      const url = `${API_BASE_URL}/permissions/${store?.link}/${store?.code
        }/users?page=${currentPage}&limit=${pageSize}&search=${encodeURIComponent(
          searchTerm,
        )}`;

      const response = await axiosInstance.get(url);

      setOperators((prev: any) =>
        loadMore ? [...prev, ...response.data.data] : response.data.data,
      );

      setPagination({
        currentPage,
        totalPages: response.data.meta.totalPages,
        pageSize,
      });
    } catch (error: any) {
      console.error("Error al cargar contactos:", error);
    } finally {
      setLoadingOperators(false);
    }
  };

  useEffect(() => {
    getOperators();
  }, []);

  const handlePopupScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.5;

    if (
      isNearBottom &&
      pagination.currentPage < pagination.totalPages &&
      !loadingOperators
    ) {
      await getOperators(true);
    }
  };

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchTerm(value);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 300),
  ).current;

  const handleSearch = (value: string) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    setOperators([]);
    getOperators(false);
  }, [searchTerm]);

  return (
    <Flex vertical className="card-permissions">
      <Text strong>
        <KeyRound style={{ marginRight: 8 }} />
        {t("label.operator")}
      </Text>
      <Flex style={{ padding: 8 }} vertical>
        <Flex gap={8} justify="space-between">
          {isNewUser ? (
            <Form.Item
              label={t("name")}
              name="firstname"
              rules={[{ required: true, message: t("label.PleaseEnter.Name") }]}
              style={{ width: "100%" }}
              initialValue={selectedContact?.firstname}
            >
              <Input autoComplete="off" />
            </Form.Item>
          ) : (
            <Flex vertical style={{ width: "100%" }}>
              <Text>{t("name")}</Text>
              <Text style={{ marginLeft: 8, fontWeight: "bold" }}>
                {selectedContact?.firstname}
              </Text>
            </Flex>
          )}

          {isNewUser ? (
            <Form.Item
              label={t("lastname")}
              name="lastname"
              rules={[{ required: true, message: t("label.PleaseEnter.Name") }]}
              style={{ width: "100%" }}
              initialValue={selectedContact?.lastname}
            >
              <Input autoComplete="off" />
            </Form.Item>
          ) : (
            <Flex vertical style={{ width: "100%" }}>
              <Text>{t("lastname")}</Text>
              <Text style={{ marginLeft: 8, fontWeight: "bold" }}>
                {selectedContact?.lastname}
              </Text>
            </Flex>
          )}
        </Flex>

        {isNewUser ? (
          <Form.Item
            label="Email"
            name="email"
            dependencies={["whatsapp"]}
            // `dependencies` hace que antd revalide este campo cuando cambia el
            // otro. Sin eso, completar el mail no limpia el error del WhatsApp
            // hasta tocarlo o hasta el submit: un error stale sobre un
            // formulario que ya es válido.
            rules={[
              { type: "email", message: "Please enter a valid email" },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  const otro = String(getFieldValue("whatsapp") ?? "").replace(/\D/g, "");
                  if (!String(value ?? "").trim() && otro.length < 11) {
                    return Promise.reject("Cargá un email o un WhatsApp");
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        ) : (
          <Flex vertical style={{ width: "100%", marginTop: 15 }}>
            <Text>Email</Text>
            <Text style={{ marginLeft: 8, fontWeight: "bold" }}>
              {selectedContact?.email}
            </Text>
          </Flex>
        )}

        {isNewUser ? (
          /**
           * Mail y WhatsApp: **al menos uno**, no los dos.
           *
           * Antes los dos eran `required`, y eso no era sólo una molestia:
           * **fabricaba un conflicto de identidad.** El backend
           * (`users.service.ts`, `CONFLICT_EMAIL_DIFFERENT_WHATSAPP`) rechaza el
           * alta cuando el mail matchea una identidad del ecosistema que YA
           * tiene teléfono y el tipeado es otro — con razón, porque no puede
           * elegir cuál vale. Obligar a tipear teléfono empujaba a ese choque a
           * cualquiera que diera de alta como operador a una persona que ya
           * existía en otro store.
           *
           * Con uno solo alcanza, ese caso se resuelve: se manda el mail, el
           * backend reusa la identidad y la ficha se crea en este store. Es la
           * razón por la que el alta de contactos "andaba" y esta no: ahí el
           * teléfono es opcional.
           *
           * El validador va en los DOS campos y mira al otro con `getFieldValue`
           * para que el error se limpie al completar cualquiera de los dos.
           */
          <Form.Item
            label="Whatsapp"
            name="whatsapp"
            dependencies={["email"]}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  const digits = String(value ?? "").replace(/\D/g, "");
                  const hayMail = !!String(getFieldValue("email") ?? "").trim();
                  if (!digits || digits.length <= 2) {
                    // Vacío (o sólo el prefijo del país) es válido si hay mail.
                    return hayMail
                      ? Promise.resolve()
                      : Promise.reject("Cargá un email o un WhatsApp");
                  }
                  if (digits.length < 11) {
                    return Promise.reject("El número debe tener al menos 11 dígitos");
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <PhoneNumberInput />
          </Form.Item>
        ) : (
          <Flex vertical style={{ width: "100%", margin: "15px 0" }}>
            <Text>Whatsapp</Text>
            <Text style={{ marginLeft: 8, fontWeight: "bold" }}>
              {selectedContact?.whatsapp || "-"}
            </Text>
          </Flex>
        )}

        <Form.Item
          label={t("userPermissions.select.titleFrom")}
          name="selectedOperator"
          style={{ minWidth: "35%" }}
        >
          <Select
            placeholder={t("labeldropdown.selectoperator")}
            loading={loadingOperators}
            showSearch
            allowClear
            onSearch={handleSearch}
            onPopupScroll={handlePopupScroll}
            style={{ width: "100%" }}
            onDropdownVisibleChange={(open) => {
              if (open && operators.length === 0) {
                getOperators(false);
              }
            }}
            optionLabelProp="label"
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={
              loadingOperators ? (
                <Flex justify="center" align="center" style={{ padding: 16 }}>
                  <Spin size="small" />
                </Flex>
              ) : (
                <div style={{ padding: 16, textAlign: "center" }}>
                  {searchTerm
                    ? t('label.no.operators', { searchTerm })
                    : "No hay operadores disponibles"}
                </div>
              )
            }
          >
            {operators.map((operator: any) => (
              <Option
                key={operator?.user?.userId}
                value={operator?.user?.userId}
                label={`${operator?.user?.firstname} ${operator?.user?.lastname}`}
              >
                <Flex vertical gap={4}>
                  <Text strong style={{ fontSize: 16 }}>
                    {operator?.user?.firstname} {operator?.user?.lastname}
                  </Text>
                  <Flex gap={8} align="center">
                    <MailOutlined style={{ fontSize: 11 }} />
                    <Text type="secondary">{operator?.user?.email || "-"}</Text>
                  </Flex>
                  <Flex gap={8} align="center">
                    <WhatsAppOutlined style={{ fontSize: 11 }} />
                    <Text type="secondary">
                      {operator?.user?.whatsapp || "-"}
                    </Text>
                  </Flex>
                </Flex>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label={t("label.message")} name="message">
          <Input.TextArea disabled rows={3} />
        </Form.Item>
      </Flex>
    </Flex>
  );
};
